import { useRef, useState } from 'react'
import { drawSegment, findTouch, getCanvasPoint, matchesCanvas, pushUndoSnapshot, undoLastChange } from '../utilities/canvasDrawing'

// Desenho à mão livre nos canvas do prontuário, igual nos dois desenhos (Locais dos sintomas e Exame complementar):
// traço contínuo com o mouse ou com o dedo e Desfazer passo a passo. Devolve os eventos para o <canvas>, o estado do
// botão Desfazer e as funções que a tela usa no Limpar marcações e ao carregar a imagem de fundo
export function useCanvasDrawing(canvasRef, color) {
  // Traço em andamento: touchId é o dedo que desenha (null no mouse) e last é o ponto anterior (null quando o mouse
  // saiu do desenho com o botão apertado: na volta, o traço recomeça no ponto de entrada)
  const strokeRef = useRef(null)
  // Cópias do desenho antes de cada alteração, da mais antiga para a mais recente
  const historyRef = useRef([])
  const [undoSteps, setUndoSteps] = useState(0)
  // Tira os listeners da janela que esperam o botão do mouse ser solto (eles também saem sozinhos quando ele é solto)
  const stopWaitingReleaseRef = useRef(null)

  const stopWaitingRelease = () => {
    stopWaitingReleaseRef.current?.()
    stopWaitingReleaseRef.current = null
  }

  const endStroke = () => {
    strokeRef.current = null
    stopWaitingRelease()
  }

  const pushHistory = () => {
    pushUndoSnapshot(historyRef.current, canvasRef.current)
    setUndoSteps(historyRef.current.length)
  }

  // Antes de uma alteração feita fora do traço (Limpar marcações): termina um traço em andamento e guarda a cópia.
  // Devolve a cópia, para o Limpar desistir dela se não havia nada para limpar (discardUndoStepIfUnchanged)
  const saveUndoStep = () => {
    endStroke()
    pushHistory()
    return historyRef.current[historyRef.current.length - 1]
  }

  // Limpar marcações sem nada para limpar: a cópia guardada é igual ao desenho de agora e não vira passo do Desfazer
  const discardUndoStepIfUnchanged = (snapshot) => {
    const history = historyRef.current
    if (!snapshot || history[history.length - 1] !== snapshot || !matchesCanvas(snapshot, canvasRef.current)) return

    history.pop()
    setUndoSteps(history.length)
  }

  const undo = () => {
    endStroke() // um traço em andamento (outro dedo apertou o Desfazer) termina aqui
    undoLastChange(historyRef.current, canvasRef.current)
    setUndoSteps(historyRef.current.length)
  }

  // A imagem de fundo carregou: um traço em andamento termina e as cópias anteriores não valem mais
  const resetUndo = () => {
    endStroke()
    historyRef.current = []
    setUndoSteps(0)
  }

  const continueStroke = (source) => {
    const stroke = strokeRef.current
    const canvas = canvasRef.current
    const point = getCanvasPoint(canvas, source)
    drawSegment(canvas.getContext('2d'), stroke.last ?? point, point, color)
    stroke.last = point
  }

  const beginStroke = (source, touchId) => {
    pushHistory()
    strokeRef.current = { touchId, last: null }
    continueStroke(source)
  }

  const isMouseStroke = () => strokeRef.current?.touchId === null
  const isTouchStroke = () => strokeRef.current !== null && strokeRef.current.touchId !== null

  // O traço do mouse termina quando o botão principal é solto, dentro ou fora do desenho. Soltar o direito ou o do
  // meio com o principal apertado não termina. O pointerup cobre o botão solto sobre um campo desabilitado, onde o
  // navegador não dispara mouseup
  const endMouseStrokeOnRelease = (event) => {
    if (event.pointerType === 'touch' || (event.buttons & 1) !== 0) return
    if (isMouseStroke()) endStroke()
  }

  const waitRelease = () => {
    stopWaitingRelease()
    window.addEventListener('mouseup', endMouseStrokeOnRelease)
    window.addEventListener('pointerup', endMouseStrokeOnRelease)
    stopWaitingReleaseRef.current = () => {
      window.removeEventListener('mouseup', endMouseStrokeOnRelease)
      window.removeEventListener('pointerup', endMouseStrokeOnRelease)
    }
  }

  const onMouseDown = (event) => {
    event.preventDefault()
    if (event.button > 0) return // só o botão principal do mouse desenha

    beginStroke(event, null)
    waitRelease()
  }

  const onMouseMove = (event) => {
    if (!isMouseStroke()) return

    // Botão solto fora da janela (ex.: troca de janela no meio do traço): o traço termina aqui
    if (event.buttons === 0) {
      endStroke()
      return
    }
    continueStroke(event)
  }

  const onMouseLeave = (event) => {
    if (!isMouseStroke() || !strokeRef.current.last) return

    if (event.buttons === 0) {
      endStroke()
      return
    }
    continueStroke(event) // leva o traço até a borda (o canvas corta o que fica fora)
    strokeRef.current.last = null
  }

  // Botão direito no meio de um traço: o menu do navegador não abre por cima do desenho
  const onContextMenu = (event) => {
    if (isMouseStroke()) event.preventDefault()
  }

  const onTouchStart = (event) => {
    if (strokeRef.current) return // outro dedo ou a palma com um traço em andamento: o traço continua como está

    const touch = event.changedTouches[0]
    if (touch) beginStroke(touch, touch.identifier)
  }

  const onTouchMove = (event) => {
    if (!isTouchStroke()) return

    const touch = findTouch(event.changedTouches, strokeRef.current.touchId)
    if (touch) continueStroke(touch)
  }

  const onTouchEnd = (event) => {
    if (isTouchStroke() && findTouch(event.changedTouches, strokeRef.current.touchId)) endStroke()
  }

  return {
    canvasHandlers: {
      onMouseDown,
      onMouseMove,
      onMouseUp: endMouseStrokeOnRelease,
      onMouseLeave,
      onContextMenu,
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onTouchCancel: onTouchEnd,
    },
    canUndo: undoSteps > 0,
    saveUndoStep,
    discardUndoStepIfUnchanged,
    undo,
    resetUndo,
  }
}
